import { accessToken } from './auth';

export * from '../../shared/types';

export const API = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.scai.fm/v1';

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export async function api<T = any>(path: string, init: RequestInit & { json?: unknown; raw?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const tok = await accessToken();
  if (tok) headers.set('Authorization', `Bearer ${tok}`);
  let body = init.body;
  if (init.json !== undefined) { headers.set('Content-Type', 'application/json'); body = JSON.stringify(init.json); }
  const r = await fetch(API + path, { ...init, headers, body });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try {
      const j = await r.json();
      const d = j.detail;
      msg = typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: any) => x.msg).join('; ') : d?.detail ?? JSON.stringify(d);
    } catch { /* not json */ }
    throw new ApiError(r.status, msg);
  }
  if (init.raw) return r as unknown as T;
  return (r.status === 204 ? null : await r.json()) as T;
}

export type Me = { sub: string; email: string | null; name: string | null;
  bindings: { role: string; partner: Partner | null; customer: { id: string; name: string } | null }[] };
export type Partner = { id: string; slug: string; name: string; branding: any; status: string; billing_mode: 'partner' | 'customer'; scaicontrol_partner_id: string | null };
export type BillingProfile = { company_name?: string; address_line1?: string; address_line2?: string; postal_code?: string; city?: string;
  country_code?: string; vat_number?: string; invoice_email?: string; language?: string };
export type Customer = { id: string; partner_id: string; name: string; tier: string; zone_limit: number; status: string; zone_count: number; role: string | null;
  station_limit: number; station_count: number; dj_allowed: boolean; tape_songs_month: number; tape_used: number; tape_to_station: boolean; tape_house: boolean;
  suspended_reason: 'partner' | 'unpaid' | null; trial_ends_at: string | null; billing_mode: 'partner' | 'customer'; billing_profile: BillingProfile | null;
  billing_profile_missing: string[]; payment_status: 'none' | 'ok' | 'past_due' | 'unpaid'; billing_linked: boolean; billing_error: string | null };
export type Zone = { id: string; customer_id: string; name: string; volume_ceiling: number; timezone: string; status: string; playlist: string[];
  playlist_kind: 'auto' | 'manual' | null; genre_mix: Record<string, { genre: string; weight: number }[]>; constraints: Record<string, any> | null;
  daypart_constraints: Record<string, Record<string, any>>; schedule: Slot[]; follows_zone_id: string | null; followers: { id: string; name: string }[] };
/** A weekly opening slot. weekday 1 = Monday; an end at or before the start runs past midnight. Times are "HH:MM[:SS]". */
export type Slot = { weekday: number; start: string; end: string; daypart: string };
export type ZoneSchedule = { always_open: boolean; open: boolean; daypart: string | null; next_change_at: string | null; hours_today: string[] };
/** "evening" -> "Evening", "late-night" -> "Late night" */
export type Track = { id: string; title: string; artist: string; album?: string | null; duration_ms: number; status: string; status_detail: string | null;
  genres: { genre: string; weight: number }[]; analysis?: { tempo_bpm: number | null; key: string | null; loudness_lufs: number | null; energy: number | null };
  made_in_tape?: boolean; tape?: { version_id: string; lyrics: string; style: string; language: string; seed: number } };
export type Device = { id: string; zone_id: string | null; kind: string; name: string; last_seen_at: string | null; last_state: any; online: boolean;
  pairing_code: string | null; pairing_expires_at: string | null ; paired: boolean };
export type Token = { id: string; label: string | null; device_id: string | null; issued_at: string; expires_at: string | null; revoked_at: string | null };
export type NowItem = { kind: string; started_at: string; duration_ms: number; track: { id: string; title: string; artist: string } | null } | null;

export const fmtTime = (iso: string | null) => (iso ? new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString() : '—');

// --------------------------------------------------------------------------- public radio (operators)
/** A listener's shout to a station's DJ, with the filter's verdict and the DJ's answer (times naive UTC). */
export type Shout = { id: string; text: string; name: string | null; status: 'pending' | 'accepted' | 'rejected' | 'answered';
  reason: string | null; language: string | null; clean: string | null; created_at: string; answer: string | null;
  answer_aired_at: string | null };
/** A DJ link written by the show runner; audio_url is signed for an hour, null when the DJ has no voice yet. */
export type RadioLink = { id: string; text: string; reason: 'link' | 'top_of_hour' | 'programme_start' | 'test'; programme: string | null;
  duration_ms: number | null; audio_url: string | null; created_at: string; aired_at: string | null };
/** One entry of a station's running order (times naive UTC). */
export type StationItem = { id: number; start_at: string; duration_ms: number; kind: 'track' | 'link' | 'clip'; status: 'queued' | 'aired';
  programme: string | null; track: { id: string; title: string; artist: string } | null; link: RadioLink | null;
  reason: RadioLink['reason'] | null };
